
import React from 'react';
import { Check, Filter } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { EVENT_TYPES } from '../constants';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface EventTypeFilterProps {
  showFilters: boolean;
  setShowFilters: (show: boolean) => void;
  visibleTypes: string[];
  toggleEventType: (typeId: string) => void;
}

export const EventTypeFilter = ({
  showFilters,
  setShowFilters,
  visibleTypes,
  toggleEventType
}: EventTypeFilterProps) => {
  const activeFilterCount = EVENT_TYPES.length - visibleTypes.length;
  
  return (
    <div className="flex items-center gap-3">
      {/* Filter Toggle Button */}
      <Popover open={showFilters} onOpenChange={setShowFilters}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className={`relative transition-all duration-200 hover:shadow-md hover:scale-105 ${
              activeFilterCount > 0 
                ? 'bg-primary/10 border-primary/30 text-primary hover:bg-primary/20' 
                : 'hover:bg-muted/50'
            }`}
          >
            <Filter className="h-4 w-4 mr-2" />
            Event Types
            {activeFilterCount > 0 && (
              <div className="absolute -top-2 -right-2 bg-primary text-primary-foreground rounded-full text-xs w-5 h-5 flex items-center justify-center font-medium animate-pulse">
                {activeFilterCount}
              </div>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-72 p-0" align="end">
          <div className="bg-gradient-to-br from-card via-background to-muted/30 border border-border/50 rounded-lg shadow-lg backdrop-blur-sm">
            <div className="p-4 border-b border-border/30">
              <h4 className="font-semibold flex items-center gap-2">
                <Filter className="h-4 w-4" />
                Filter Event Types
              </h4>
              <p className="text-sm text-muted-foreground mt-1">
                Toggle event types to show or hide them
              </p>
            </div>
            <div className="p-4 space-y-2">
              {EVENT_TYPES.map((type) => {
                const IconComponent = type.icon;
                const isVisible = visibleTypes.includes(type.id);
                return (
                  <div
                    key={type.id}
                    onClick={() => toggleEventType(type.id)}
                    className={`
                      flex items-center gap-3 p-3 rounded-lg cursor-pointer
                      transition-all duration-200 hover:shadow-sm
                      ${isVisible 
                        ? 'bg-primary/10 border border-primary/30 text-primary' 
                        : 'bg-muted/30 hover:bg-muted/50 border border-transparent'
                      }
                    `}
                  >
                    <div className={`
                      flex items-center justify-center w-8 h-8 rounded-lg
                      ${isVisible ? 'bg-primary/20' : 'bg-muted'}
                    `}>
                      <IconComponent className="h-4 w-4" />
                    </div>
                    <span className="flex-1 font-medium">{type.label}</span>
                    <div className={`
                      w-5 h-5 rounded-full border-2 flex items-center justify-center
                      transition-all duration-200
                      ${isVisible 
                        ? 'bg-primary border-primary' 
                        : 'border-muted-foreground/30 hover:border-primary/50'
                      }
                    `}>
                      {isVisible && <Check className="h-3 w-3 text-primary-foreground" />}
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="p-4 border-t border-border/30 bg-muted/20">
              <Button
                variant="ghost"
                size="sm"
                className="w-full hover:bg-primary/10 hover:text-primary"
                onClick={() => {
                  EVENT_TYPES.forEach(type => {
                    if (!visibleTypes.includes(type.id)) {
                      toggleEventType(type.id);
                    }
                  });
                }}
              >
                Show All Types
              </Button>
            </div>
          </div>
        </PopoverContent>
      </Popover>

      {/* Quick Select Dropdown */}
      <Select
        value={visibleTypes.length === EVENT_TYPES.length ? "all" : 
              visibleTypes.length === 1 ? visibleTypes[0] : "custom"}
        onValueChange={(value) => {
          if (value === "all") {
            EVENT_TYPES.forEach(type => {
              if (!visibleTypes.includes(type.id)) {
                toggleEventType(type.id);
              }
            });
          } else if (value !== "custom") {
            // Show only selected event type
            EVENT_TYPES.forEach(type => {
              if (type.id !== value && visibleTypes.includes(type.id)) {
                toggleEventType(type.id);
              } else if (type.id === value && !visibleTypes.includes(type.id)) {
                toggleEventType(type.id);
              }
            });
          }
        }}
      >
        <SelectTrigger className="w-[200px] transition-all duration-200 hover:shadow-md hover:border-primary/30">
          <SelectValue placeholder="Quick filter..." />
        </SelectTrigger>
        <SelectContent className="bg-gradient-to-br from-card via-background to-muted/30 border border-border/50 backdrop-blur-sm">
          <SelectItem value="all" className="hover:bg-primary/10">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-gradient-to-r from-primary to-accent"></div>
              <span>All Events</span>
            </div>
          </SelectItem>
          {visibleTypes.length !== EVENT_TYPES.length && visibleTypes.length > 1 && (
            <SelectItem value="custom" className="hover:bg-muted/50">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-muted-foreground"></div>
                <span>Custom Selection ({visibleTypes.length})</span>
              </div>
            </SelectItem>
          )}
          {EVENT_TYPES.map((type) => {
            const IconComponent = type.icon;
            return (
              <SelectItem key={type.id} value={type.id} className="hover:bg-primary/10">
                <div className="flex items-center gap-2">
                  <IconComponent className="h-4 w-4" />
                  <span>{type.label}</span>
                </div>
              </SelectItem>
            );
          })}
        </SelectContent>
      </Select>
    </div>
  );
};
